import { memo, useCallback } from "react";
import { Grid, IconButton } from "@mui/material";
import {
  IDraftItem,
  IDraftListResponse
} from "@/src-refactor/modules/store/qrtk/services/property-draft/responses/draft-list.response";
import { MRT_TableOptions } from "material-react-table";
import { MxDataTableCellText } from "@rf-l1/data-table/MXDataTableGeneric";
import MxDataTable from "@rf-l1/data-table/MxDataTable";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useDraftDeleteMutation } from "@/src-refactor/modules/store/qrtk/services/property-draft/property-draft.qrtk";
import useAlert from "@rf-hooks/useAlert";
import { toast } from "react-hot-toast";
import { C_PATH_ROUTES } from "@/src-refactor/modules/shared/model/const/path.const";
import { HCatchServerError } from "@rf-helper/cath-error-server";
import { useFormContext } from "react-hook-form";
import HCheckArray from "@rf-helper/check-have-value";
import MxTooltipNormal from "@rf-l1/tooltip/MxTooltipNormal";
import {
  O_REALESTATE_TYPES,
  O_REALESTATE_TYPES_PROJECT
} from "@/src-refactor/modules/shared/model/const/property/real-estate-type.const";

interface PropertyDraftListComponentProps {
  data?: IDraftListResponse;
}

const PropertyDraftListComponent = memo(
  ({ data }: PropertyDraftListComponentProps) => {
    const { warningAlert } = useAlert();
    const navigate = useNavigate();
    const [DELETE_DRAFT_M] = useDraftDeleteMutation();
    const { getValues } = useFormContext();
    if (!data?.data?.length) {
      return null;
    }

    const handleDeleteDraft = useCallback(
      (draft: IDraftItem) => {
        warningAlert({
          title: "Eliminar borrador",
          message: "¿Estás seguro de querer eliminar este borrador?",
          confirmBtnMessage: "Eliminar",
          declineBtnMessage: "Cancelar"
        }).then(res => {
          if (res.type.confirm) {
            const promise = DELETE_DRAFT_M({
              params: {
                id: draft.id
              }
            })
              .unwrap()
              .then(res => {
                if (res.success) {
                  toast.success("Borrador eliminado correctamente");
                }
              });
            toast.promise(promise, {
              loading: "Eliminando borrador...",
              success: "Borrador eliminado correctamente",
              error: e => HCatchServerError(e)
            });
          }
        });
      },
      [DELETE_DRAFT_M, warningAlert]
    );

    const handleUpdateDraft = useCallback(
      (draft: IDraftItem) => {
        const path = draft.is_project
          ? C_PATH_ROUTES.REALESTATE.DRAFT_PROJECT_UPDATE(Number(draft.id))
          : C_PATH_ROUTES.REALESTATE.DRAFT_UPDATE(Number(draft.id));
        navigate(path);
      },
      [navigate]
    );

    const formatDate = useCallback((dateString: string) => {
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("es-ES", {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
      } catch {
        return dateString;
      }
    }, []);

    const handleDetailDraft = useCallback((draft: IDraftItem) => {
      // TODO: Implementar navegación a detalle
      console.log("Detail draft:", draft.id);
    }, []);

    const getData = (data: IDraftListResponse) => {
      const values = getValues();
      const typeDraft = HCheckArray(values.typeDraft)
        ? values.typeDraft[0].id
        : undefined;

      if (typeDraft || typeDraft === 0) {
        return data.data.filter(item => item.is_project === typeDraft);
      } else {
        return data.data;
      }
    };

    const options: MRT_TableOptions<IDraftItem> = {
      columns: [
        {
          header: "ID",
          accessorKey: "id",
          size: 30,
          Cell: ({ row }) => {
            return <MxDataTableCellText>{row.original.id}</MxDataTableCellText>;
          }
        },
        {
          header: "Tipo",
          accessorKey: "type",
          size: 120,
          Cell: ({ row }) => {
            const type = row.original.is_project
              ? O_REALESTATE_TYPES_PROJECT
              : O_REALESTATE_TYPES;
            return (
              <MxDataTableCellText>
                {type[row.original.type_id]?.label || "-"}
              </MxDataTableCellText>
            );
          }
        },
        {
          header: "Es Proyecto",
          accessorKey: "is_project",
          size: 100,
          Cell: ({ row }) => {
            return (
              <MxDataTableCellText>
                {row.original.is_project ? "Sí" : "No"}
              </MxDataTableCellText>
            );
          }
        },
        {
          header: "Ciudad",
          accessorKey: "city",
          size: 120,
          Cell: ({ row }) => {
            return <MxDataTableCellText>{row.original.city}</MxDataTableCellText>;
          }
        },
        {
          header: "Sector",
          accessorKey: "sector",
          size: 120,
          Cell: ({ row }) => {
            return <MxDataTableCellText>{row.original.sector}</MxDataTableCellText>;
          }
        },
        {
          header: "Creado",
          accessorKey: "created_at",
          size: 120,
          Cell: ({ row }) => {
            return (
              <MxDataTableCellText>
                {formatDate(row.original.created_at)}
              </MxDataTableCellText>
            );
          }
        },
        {
          header: "Actualizado",
          accessorKey: "updated_at",
          size: 120,
          Cell: ({ row }) => {
            return (
              <MxDataTableCellText>
                {formatDate(row.original.updated_at)}
              </MxDataTableCellText>
            );
          }
        },
        {
          header: "Acciones",
          accessorKey: "actions",
          size: 100,
          Cell: ({ row }) => {
            return (
              <Grid container>
                <Grid item xs="auto">
                  <MxTooltipNormal label="Editar borrador" placement="top">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleUpdateDraft(row.original)}
                    >
                      <IconPencil size={20} />
                    </IconButton>
                  </MxTooltipNormal>
                </Grid>
                <Grid item xs="auto">
                  <MxTooltipNormal label="Eliminar borrador" placement="top">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteDraft(row.original)}
                    >
                      <IconTrash size={20} />
                    </IconButton>
                  </MxTooltipNormal>
                </Grid>
              </Grid>
            );
          }
        }
      ],
      data: getData(data)
    };

    return (
      <Grid container mb={3}>
        <MxDataTable options={options} />
      </Grid>
    );
  }
);

PropertyDraftListComponent.displayName = "PropertyDraftListComponent";
export default PropertyDraftListComponent;
